'use client';

/**
 * Thinking ticker — audio feedback during tool execution.
 *
 * Plays a subtle tapping pattern while the AI is processing a tool call,
 * giving the user auditory feedback that something is happening.
 *
 * Audio chain: sample source → gain → convolver (muffled IR) → high-shelf → master gain → destination
 */

import { useRef, useCallback } from 'react';

// ─── Configuration ──────────────────────────────────────────────

const THINKING_SAMPLE_PATHS = [
  '/audio/tap-notification-180637.mp3',
  '/audio/tappingmic-90213.mp3',
] as const;

interface PatternEntry {
  sampleIndex: number;
  volume: number;
  delay: number;
}

const DEFAULT_THINKING_PATTERN: PatternEntry[] = [
  { sampleIndex: 1, volume: 2.0, delay: 4000 },
  { sampleIndex: 1, volume: 1.75, delay: 2000 },
];

const MASTER_GAIN = 2.0;
const REPEAT_DELAY_MS = 300;

// ─── Impulse response for muffled effect ────────────────────────

function createMuffledIR(ctx: AudioContext): AudioBuffer {
  const seconds = 0.06;
  const length = Math.floor(ctx.sampleRate * seconds);
  const ir = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = ir.getChannelData(0);

  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    const t = i / length;
    const decay = Math.exp(-12 * t);
    data[i] = white * decay;
  }
  return ir;
}

// ─── Hook ───────────────────────────────────────────────────────

export function useThinkingTicker() {
  const isThinkingRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const timerRef = useRef<number | null>(null);
  const patternIndexRef = useRef(0);
  const convolverRef = useRef<ConvolverNode | null>(null);
  const postShelfRef = useRef<BiquadFilterNode | null>(null);
  const samplesRef = useRef<AudioBuffer[]>([]);

  const startThinkingTicker = useCallback(async () => {
    if (isThinkingRef.current) return;
    isThinkingRef.current = true;
    patternIndexRef.current = 0;

    // Create or reuse AudioContext
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new AudioContext();

      // Master gain
      const gain = audioCtxRef.current.createGain();
      gain.gain.value = MASTER_GAIN;
      gain.connect(audioCtxRef.current.destination);
      masterGainRef.current = gain;

      // Convolver for muffled effect
      const convolver = audioCtxRef.current.createConvolver();
      convolver.buffer = createMuffledIR(audioCtxRef.current);
      convolverRef.current = convolver;

      // High-shelf filter for tone shaping
      const postShelf = audioCtxRef.current.createBiquadFilter();
      postShelf.type = 'highshelf';
      postShelf.frequency.value = 4000;
      postShelf.gain.value = -8;
      postShelfRef.current = postShelf;

      // Chain: convolver → postShelf → master gain
      convolver.connect(postShelf);
      postShelf.connect(gain);
    }

    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // Load samples if not already loaded
    if (samplesRef.current.length === 0) {
      const buffers: AudioBuffer[] = [];
      for (const path of THINKING_SAMPLE_PATHS) {
        try {
          const res = await fetch(path);
          const arrayBuffer = await res.arrayBuffer();
          const buffer = await ctx.decodeAudioData(arrayBuffer);
          buffers.push(buffer);
        } catch {
          // Skip failed samples — ticker still works with remaining ones
        }
      }
      samplesRef.current = buffers;
    }

    if (samplesRef.current.length === 0) return;

    // Pattern playback loop
    const playNextInPattern = () => {
      if (!isThinkingRef.current || !audioCtxRef.current) return;

      const pattern = DEFAULT_THINKING_PATTERN;
      const idx = patternIndexRef.current % pattern.length;
      const entry = pattern[idx];
      const sample = samplesRef.current[entry.sampleIndex];

      if (sample && convolverRef.current) {
        const source = audioCtxRef.current.createBufferSource();
        source.buffer = sample;

        const noteGain = audioCtxRef.current.createGain();
        noteGain.gain.value = entry.volume;

        source.connect(noteGain);
        noteGain.connect(convolverRef.current);
        source.start();
      }

      patternIndexRef.current++;

      // At end of pattern, add repeat delay
      const isLastInCycle = patternIndexRef.current % pattern.length === 0;
      const nextDelay = isLastInCycle
        ? entry.delay + REPEAT_DELAY_MS
        : entry.delay;

      timerRef.current = window.setTimeout(playNextInPattern, nextDelay);
    };

    playNextInPattern();
  }, []);

  const stopThinkingTicker = useCallback(async () => {
    isThinkingRef.current = false;

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Disconnect FX nodes
    try {
      convolverRef.current?.disconnect();
      postShelfRef.current?.disconnect();
    } catch {
      // Already disconnected
    }
    convolverRef.current = null;
    postShelfRef.current = null;

    // Fade out and close
    if (masterGainRef.current && audioCtxRef.current) {
      const now = audioCtxRef.current.currentTime;
      masterGainRef.current.gain.setValueAtTime(
        masterGainRef.current.gain.value,
        now
      );
      masterGainRef.current.gain.linearRampToValueAtTime(0, now + 0.15);

      await new Promise((r) => setTimeout(r, 200));
      try {
        await audioCtxRef.current.close();
      } catch {
        // Already closed
      }
      audioCtxRef.current = null;
      masterGainRef.current = null;
    }

    patternIndexRef.current = 0;
  }, []);

  return {
    isThinkingRef,
    startThinkingTicker,
    stopThinkingTicker,
  };
}
